import React, { FC, ReactNode } from "react";
import { Handle, NodeProps, Position } from "reactflow";
import { useAppSelector } from "store/hooks";
import { MessageType } from "types/Workflow";
import {
  InAppMessageIcon,
  EmailIcon,
  PushIcon,
  SlackIcon,
  SMSIcon,
  WebhookIcon,
} from "../Icons";
import { MessageNodeData, Stats } from "./NodeData";
import { NodeDevModeHighlighter } from "./NodeDevModeHighlighter";

const compaсtNumberFormatter = Intl.NumberFormat("en", { notation: "compact" });

export const messageFixtures: Record<
  MessageType,
  {
    icon: ReactNode;
    text: string;
    statsToShow?: {
      key: keyof Stats;
      name: string;
      renderLabel: (value: number) => string;
    }[];
  }
> = {
  [MessageType.EMAIL]: {
    icon: <EmailIcon />,
    text: "Email",
    statsToShow: [
      {
        key: "sent",
        name: "Sent",
        renderLabel: (value) => compaсtNumberFormatter.format(value),
      },
      {
        key: "delivered",
        name: "Delivered",
        renderLabel: (value) => compaсtNumberFormatter.format(value),
      },
      {
        key: "openedPercentage",
        name: "Opened",
        renderLabel: (value) => `${compaсtNumberFormatter.format(value)}%`,
      },
      {
        key: "clickedPercentage",
        name: "Clicked",
        renderLabel: (value) => `${compaсtNumberFormatter.format(value)}%`,
      },
    ],
  },
  [MessageType.IN_APP]: {
    icon: <InAppMessageIcon />,
    text: "In App Message",
  },
  [MessageType.PUSH]: {
    icon: <PushIcon />,
    text: "Push",
    statsToShow: [
      {
        key: "sent",
        name: "Sent",
        renderLabel: (value) => compaсtNumberFormatter.format(value),
      },
      {
        key: "delivered",
        name: "Delivered",
        renderLabel: (value) => compaсtNumberFormatter.format(value),
      },
      {
        key: "openedPercentage",
        name: "Opened",
        renderLabel: (value) => `${compaсtNumberFormatter.format(value)}%`,
      },
    ],
  },
  [MessageType.SLACK]: {
    icon: <SlackIcon />,
    text: "Slack",
  },
  [MessageType.SMS]: {
    icon: <SMSIcon />,
    text: "SMS",
    statsToShow: [
      {
        key: "sent",
        name: "Sent",
        renderLabel: (value) => compaсtNumberFormatter.format(value),
      },
      {
        key: "delivered",
        name: "Delivered",
        renderLabel: (value) => compaсtNumberFormatter.format(value),
      },
    ],
  },
  [MessageType.WEBHOOK]: {
    icon: <WebhookIcon />,
    text: "Webhook",
    statsToShow: [
      {
        key: "wssent",
        name: "Sent",
        renderLabel: (value) => compaсtNumberFormatter.format(value),
      },
      {
        key: "failed",
        name: "Failed",
        renderLabel: (value) => `${compaсtNumberFormatter.format(value)}%`,
      },
    ],
  },
};

const unknownMessageFixtures: { icon: ReactNode; text: string } = {
  icon: <></>,
  text: "Unknown message type",
};

export const MessageNode: FC<NodeProps<MessageNodeData>> = ({
  isConnectable,
  data,
  selected,
  id,
}) => {
  const { isViewMode } = useAppSelector((state) => state.flowBuilder);

  const { template, stats, disabled } = data;
  const specificRecipientCount =
    (data.internalRecipientGroupIds?.length ?? 0) +
    (data.additionalEmails?.length ?? 0) +
    (data.dynamicRecipientSources?.length ?? 0) +
    (data.additionalPhoneNumbers?.length ?? 0);
  const specificRecipientLabel =
    template?.type === MessageType.SMS ? "phone number" : "specific recipient";

  const nodeFixtures: {
    icon: ReactNode;
    text: string;
    statsToShow?: {
      key: keyof Stats;
      name: string;
      renderLabel: (value: number) => string;
    }[];
  } = template
    ? messageFixtures[template.type] || unknownMessageFixtures
    : unknownMessageFixtures;

  return (
    <div
      className={`relative message-node ${
        isViewMode ? "w-[300px]" : "w-[260px]"
      } ${
        isViewMode && stats && nodeFixtures.statsToShow
          ? "h-[140px]"
          : "h-[80px]"
      } rounded bg-themed-card ${
        disabled ? "opacity-50 cursor-not-allowed" : ""
      } ${
        selected ? "border-2 border-[#6366F1]" : "border border-themed-border"
      }`}
      onDragOver={(e) => {
        e.stopPropagation();
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        console.log(e.dataTransfer.getData("jumpTo"));
      }}
    >
      <NodeDevModeHighlighter id={id} />
      <Handle
        position={Position.Top}
        type="target"
        isConnectable={isConnectable}
        className="!min-h-[1px] !h-[1px] !top-[-1px] !opacity-0 !border-0 !pointer-events-none !cursor-default"
      />
      <div
        className="p-[16px] flex flex-col gap-[2px]"
        onDragOver={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          console.log(e.dataTransfer.getData("jumpTo"));
        }}
      >
        <div className="flex gap-[6px]">
          <div className="text-[#6366F1]">{nodeFixtures.icon}</div>
          <div
            className={`font-inter whitespace-nowrap overflow-hidden text-ellipsis font-semibold text-base ${
              nodeFixtures === unknownMessageFixtures ? "text-red-500" : ""
            }`}
          >
            {data?.customName || nodeFixtures.text}
          </div>
        </div>
        <div className="font-inter font-normal text-[14px] leading-[22px] whitespace-nowrap text-ellipsis max-w-full overflow-hidden">
          {template?.selected ? (
            <>
              <span className="#4B5563">Send </span>
              <span className="font-medium">{template.selected.name}</span>
              {data.recipientMode === "internal" && (
                <span>
                  {" "}
                  to{" "}
                  {specificRecipientCount > 0
                    ? `${specificRecipientCount} ${specificRecipientLabel}${
                        specificRecipientCount === 1 ? "" : "s"
                      }`
                    : template?.type === MessageType.SMS
                    ? "specific phone numbers"
                    : "specific recipients"}
                </span>
              )}
              {data.recipientMode !== "internal" && data.alsoNotifyInternal && (
                <span className="text-themed-textSecondary">
                  {" "}
                  + notifies management/agents
                </span>
              )}
            </>
          ) : (
            <span className={data.showErrors ? "text-[#F43F5E]" : ""}>
              Select a template
            </span>
          )}
        </div>
      </div>
      {isViewMode && stats && nodeFixtures.statsToShow && (
        <div className="px-[16px] py-[6px] flex justify-between gap-[10px] border-t-[1px] border-themed-border">
          {nodeFixtures.statsToShow.map((stat, i) => (
            <div
              key={i}
              className={`${stat.key} h-[46px] w-full flex flex-col gap-[4px] ${
                i !== (nodeFixtures.statsToShow?.length || 1) - 1
                  ? "border-r-[1px] border-themed-border"
                  : ""
              }`}
            >
              <div className="text-[12px] leading-5 text-themed-textSecondary">
                {stat.name}
              </div>
              <div className="stat-result font-semibold text-[14px] leading-[22px]">
                {stat.renderLabel(stats[stat.key] || 0)}
              </div>
            </div>
          ))}
        </div>
      )}

      <Handle
        position={Position.Bottom}
        type="source"
        isConnectable={isConnectable}
        className="!min-h-[1px] !h-[1px] !bottom-[1px] !opacity-0 !border-0 !pointer-events-none !cursor-default"
      />
    </div>
  );
};
